import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  AuthError, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  sendPasswordResetEmail, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signOut as signOutFirebase,
  updateProfile,
} from 'firebase/auth'

import { auth, firestore } from '..'
import { useAuth } from '../providers/auth'
import { doc, setDoc } from "firebase/firestore";

interface ErrorTypes {
  name?: string
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  other?: string
}

function authError(errorCode: string) {
  switch (errorCode) {
    case 'auth/invalid-email':
      return { email: 'Du har inte skrivit en giltig e-post' }

    case 'auth/user-not-found':
      return { email: 'E-post kunde inte hittas' }

    case 'auth/missing-email':
      return { email: 'Du måste fylla i e-post' }

    case 'auth/operation-not-allowed':
      return { other: 'Google-inloggning är inte aktiverad i Firebase' }

    case 'auth/unauthorized-domain':
      return { other: 'Domänen är inte godkänd för Firebase Authentication' }

    case 'auth/popup-closed-by-user':
      return { other: 'Google-inloggningen avbröts' }

    default:
      return { other: 'Någonting gick fel, försök igen' }
  
  }
}

export function useSignInEmail(): [
  (email: string, password: string) => void, 
  boolean, 
  ErrorTypes | null,
  () => void
] {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const signInEmail = async (email: string, password: string) => {
    setLoading(true)

    try {
      await signInWithEmailAndPassword(auth, email, password)
      setLoading(false)
    } catch (e) {
      const error = e as AuthError
      setError(authError(error.code))
      setLoading(false)
    }
  }

  return [signInEmail, loading, error, clearError]
}

export function useResetPassword(): [
  (email: string) => void,
  boolean,
  ErrorTypes | null,
  () => void
] {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const sendResetEmail = async (email: string) => {
    setLoading(true)

    try {
      await sendPasswordResetEmail(auth, email)
      setLoading(false)
    } catch (e) {
      const error = e as AuthError
      setError(authError(error.code))
      setLoading(false)
    }
  }

  return [sendResetEmail, loading, error, clearError]
}

export function useRegisterEmail(): [
  (email: string, password: string, firstName: string, lastName: string) => void,
  boolean,
  ErrorTypes | null,
  () => void
] {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const registerEmail = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    setLoading(true)

    try {
      const trimmedEmail = email.trim()
      const trimmedFirstName = firstName.trim()
      const trimmedLastName = lastName.trim()

      if (!trimmedFirstName) {
        setError({ firstName: 'Du måste fylla i förnamn' })
        setLoading(false)
        return
      }

      if (!trimmedLastName) {
        setError({ lastName: 'Du måste fylla i efternamn' })
        setLoading(false)
        return
      }

      if (!trimmedEmail) {
        setError({ email: 'Du måste fylla i e-post' })
        setLoading(false)
        return
      }

      const displayName = `${trimmedFirstName} ${trimmedLastName}`
      const res = await createUserWithEmailAndPassword(
        auth,
        trimmedEmail,
        password
      )
      await updateProfile(res.user, { displayName })
      const docRef = doc(firestore, 'users', res.user.uid)
      await setDoc(
        docRef,
        {
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          displayName,
          email: trimmedEmail,
          credits: 0,
        },
        { merge: true }
      )

      setLoading(false)
    } catch (e) {
      const err = e as AuthError
      switch (err.code) {
        case 'auth/invalid-email':
          setError({ email: 'Du har inte skrivit en giltig e-post' })
          break

        case 'auth/missing-email':
          setError({ email: 'Du måste fylla i e-post' })
          break

        case 'auth/email-already-in-use':
          setError({ email: 'Det finns redan ett konto med den e-posten' })
          break

        case 'auth/missing-password':
          setError({ password: 'Du måste fylla i lösenord' })
          break

        case 'auth/weak-password':
          setError({ password: 'Lösenordet behöver vara minst 6 tecken' })
          break

        case 'auth/user-not-found':
          setError({ email: 'E-post kunde inte hittas' })
          break

        default:
          setError({ other: 'Någonting gick fel, försök igen' })
      }

      setLoading(false)
    }
  }

  return [registerEmail, loading, error, clearError]
}

export function useSignInGoogle(): [
  () => void,
  boolean,
  ErrorTypes | null,
  () => void
] {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const signInGoogle = async () => {
    setLoading(true)
    setError(null)

    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      await signInWithPopup(auth, provider)
      setLoading(false)
    } catch (e) {
      const error = e as AuthError
      setError(authError(error.code))
      setLoading(false)
    }
  }

  return [signInGoogle, loading, error, clearError]
}

export function useSignOut(redirect?: string): [() => void, boolean, string] {
  const { setUser, setAdmin } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const clearAuthState = () => {
    setUser(null)
    setAdmin(false)
    setLoading(false)
  }

  const signOut = () => {
    setLoading(true)

    signOutFirebase(auth).then(
      () => {
        navigate(redirect ? redirect : '/login')
        clearAuthState()
      }
    ).catch(error => {
      setError(error.message)
      clearAuthState()
    })
  }

  return [signOut, loading, error]
}
